/**
 * createCopilotClient — factory wrapping the native browser WebSocket for the
 * Champ Copilot chat channel. Implements the frozen contract:
 *   • subprotocol auth ["copilot.chat.v1", "x-access-token.<token>"]
 *   • start / resume, one user turn at a time
 *   • ping keepalive (~25s) on idle
 *   • reconnect + resume after a working session; never loop on a pre-`ready`
 *     (403/auth) failure
 *   • never block on an orphaned turn_id after resume
 * See docs/copilot/stage6/copilot-ws-client-integration.md (§7).
 */
import config from '@config/index'
import { FinalFrame, ServerFrame, TraceEvent, CopilotStatus } from './copilotTypes'

const SUBPROTOCOL = 'copilot.chat.v1'
const PING_INTERVAL_MS = 25000
const FIRST_BACKOFF_MS = 1000
const MAX_BACKOFF_MS = 30000

export interface CopilotClientParams {
  /** raw access token (getJsonCookies()), rides as the 2nd subprotocol */
  token: string
  /** override the endpoint; defaults to config.copilot_ws_url */
  url?: string
  /** resume an existing conversation instead of starting a new one */
  sessionId?: string | null
  onStatus?: (status: CopilotStatus) => void
  onReady?: (sessionId: string) => void
  onAck?: (turnId: string, clientMsgId?: string) => void
  onTrace?: (event: TraceEvent, turnId: string) => void
  onPartial?: (text: string, turnId: string) => void
  onFinal?: (frame: FinalFrame) => void
  onError?: (message: string, turnId?: string) => void
  /** handshake/auth failed (403 or closed before any `ready`) — do NOT retry */
  onAuthFailure?: () => void
}

export interface CopilotClient {
  send: (text: string, clientMsgId?: string) => void
  close: () => void
  getSessionId: () => string | null
}

const noop = () => undefined

export const createCopilotClient = (params: CopilotClientParams): CopilotClient => {
  const url = params.url || (config as { copilot_ws_url?: string }).copilot_ws_url || ''

  // normalise handlers so we never call through optional chaining
  const onStatus = params.onStatus || noop
  const onReady = params.onReady || noop
  const onAck = params.onAck || noop
  const onTrace = params.onTrace || noop
  const onPartial = params.onPartial || noop
  const onFinal = params.onFinal || noop
  const onError = params.onError || noop
  const onAuthFailure = params.onAuthFailure || noop

  let ws: WebSocket | null = null
  let sessionId: string | null = params.sessionId || null
  let everReady = false
  let awaitingTurnId: string | null = null
  let backoff = FIRST_BACKOFF_MS
  let pingTimer: ReturnType<typeof setInterval> | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let closedByUser = false

  const safeSend = (frame: object) => {
    try {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(frame))
    } catch (err) {
      /* socket gone — ignore */
    }
  }

  const stopPing = () => {
    if (pingTimer) {
      clearInterval(pingTimer)
      pingTimer = null
    }
  }
  const startPing = () => {
    stopPing()
    pingTimer = setInterval(() => safeSend({ type: 'ping' }), PING_INTERVAL_MS)
  }

  const open = (resume: boolean) => {
    onStatus(resume ? 'reconnecting' : 'connecting')
    let socket: WebSocket
    try {
      socket = new WebSocket(url, [SUBPROTOCOL, `x-access-token.${params.token}`])
    } catch (err) {
      onStatus('error')
      onAuthFailure()
      return
    }
    ws = socket

    socket.onopen = () => {
      if (resume && sessionId) {
        safeSend({ type: 'resume', session_id: sessionId, last_turn_id: awaitingTurnId || undefined })
        awaitingTurnId = null // a turn mid-flight when we dropped will never produce a final
      } else {
        safeSend({ type: 'start', session_id: sessionId || undefined })
      }
      startPing()
    }

    socket.onmessage = (e: MessageEvent) => {
      let m: ServerFrame
      try {
        m = JSON.parse(e.data)
      } catch (err) {
        return
      }
      switch (m.type) {
        case 'ready':
          sessionId = m.session_id
          everReady = true
          backoff = FIRST_BACKOFF_MS
          onStatus('ready')
          onReady(m.session_id)
          break
        case 'ack':
          awaitingTurnId = m.turn_id
          onAck(m.turn_id, m.client_msg_id)
          break
        case 'trace':
          onTrace(m.event, m.turn_id)
          break
        case 'partial':
          onPartial(m.text, m.turn_id)
          break
        case 'final':
          awaitingTurnId = null
          onFinal(m)
          break
        case 'error':
          onError(m.message, m.turn_id)
          break
        case 'pong':
          break
        case 'reconnect':
          try {
            socket.close()
          } catch (err) {
            /* will resume via onclose */
          }
          break
        default:
          break
      }
    }

    socket.onerror = () => {
      /* surfaced through onclose */
    }

    socket.onclose = () => {
      stopPing()
      ws = null
      if (closedByUser) {
        onStatus('closed')
        return
      }
      if (!everReady) {
        // 403 / closed before any `ready` → auth/config problem, do not loop
        onStatus('error')
        onAuthFailure()
        return
      }
      // unexpected close after a working session → reopen + resume with backoff
      onStatus('reconnecting')
      reconnectTimer = setTimeout(() => {
        backoff = Math.min(backoff * 2, MAX_BACKOFF_MS)
        open(true)
      }, backoff)
    }
  }

  const send = (text: string, clientMsgId?: string) => {
    safeSend({ type: 'user_message', text, client_msg_id: clientMsgId })
  }

  const close = () => {
    closedByUser = true
    stopPing()
    if (reconnectTimer) clearTimeout(reconnectTimer)
    try {
      if (ws) ws.close()
    } catch (err) {
      /* ignore */
    }
    ws = null
  }

  // resume if we were handed a prior session id, otherwise start fresh
  open(Boolean(sessionId))

  return { send, close, getSessionId: () => sessionId }
}
